// The Sundays Are Festival shot library — the reference set behind the
// photography SOP. Frames live in public/creative/frames/<id>.jpg.
//
// Every entry is a real delivered frame with a note explaining why it works.
// The tags map to the four promises a Sunday gallery has to prove; `smile`
// marks frames that clear the one-in-three Smile Mandate, which is what the
// meter on the page counts.

export const CREATIVE_HUB_URL = "https://creative.mycllxtv.com";

/** The four promises. A gallery is not delivered until all four are in it. */
export const PROMISES = {
  "F": {
    "name": "Best friend",
    "line": "You can find your best friend here."
  },
  "J": {
    "name": "Joy",
    "line": "You can find the joy you were looking for."
  },
  "U": {
    "name": "Fun",
    "line": "You can find the fun you were looking for."
  },
  "B": {
    "name": "Breakthrough",
    "line": "You can find your breakthrough here."
  }
};

/** Shot types, in the order a Sunday actually happens. */
export const SECTIONS = [
  {
    "id": "arrival",
    "name": "Arrival",
    "kicker": "The parking lot and the door",
    "body": "Nobody is guarded yet and everybody is greeting. The most under-shot and most valuable stretch of the day.",
    "quota": "20+ frames before the first song"
  },
  {
    "id": "room",
    "name": "The Room",
    "kicker": "Scale and atmosphere",
    "body": "A full room sells a full room. Take these, then put the wide lens back in the bag.",
    "quota": "6-8 frames, then move on"
  },
  {
    "id": "worship",
    "name": "Worship",
    "kicker": "The response, not the stage",
    "body": "Point the camera at row three. The engine of the entire gallery lives here.",
    "quota": "30+ frames"
  },
  {
    "id": "stage",
    "name": "The Stage",
    "kicker": "Team and the Word",
    "body": "Necessary, but capped. The stage is not the story - every extra stage frame is a lobby frame we didn't get.",
    "quota": "15 frames. Hard ceiling of 25"
  },
  {
    "id": "tribe",
    "name": "The Tribe",
    "kicker": "You can find your best friend here",
    "body": "If we only had time for one section, it would be this one.",
    "quota": "25+ frames"
  },
  {
    "id": "breakthrough",
    "name": "Breakthrough",
    "kicker": "The altar",
    "body": "Different posture. Quieter, longer lens, more distance. You are a witness here, not a director.",
    "quota": "10-12 frames"
  },
  {
    "id": "after",
    "name": "After",
    "kicker": "Lobby and exit",
    "body": "People are unlocked after service. Second-highest value window of the day.",
    "quota": "20+ frames"
  },
  {
    "id": "texture",
    "name": "Texture",
    "kicker": "Details, all day",
    "body": "Fills Carousel Slot 03 and every gap in a grid. Shoot these while you walk.",
    "quota": "10 frames"
  }
];

/** 110 reference frames. */
export const FRAMES = [
 {
  "id": "0419",
  "sec": "arrival",
  "tags": [
   "F",
   "U"
  ],
  "smile": 1,
  "note": "Two strangers, ten seconds in. The thumbs-up is corny and that is exactly why it works — nobody guards their face at a thumbs-up."
 },
 {
  "id": "0420",
  "sec": "arrival",
  "tags": [
   "U"
  ],
  "smile": 0,
  "note": "The establishing frame. Shoot the tent, the table, the sky. This is the only wide the arrival section needs."
 },
 {
  "id": "0421",
  "sec": "arrival",
  "tags": [
   "F",
   "U"
  ],
  "smile": 1,
  "note": "Shoot the high-five at the top of the arc, not after. Half a second late and it's two people standing near a wall."
 },
 {
  "id": "0424",
  "sec": "arrival",
  "tags": [
   "U"
  ],
  "smile": 0,
  "note": "Outside, before doors. Palms, light, a hundred people who chose to get here early. Sell the pre-party."
 },
 {
  "id": "0426",
  "sec": "arrival",
  "tags": [
   "U"
  ],
  "smile": 0,
  "note": "Backlight through the walkway. Expose for their faces and let the background blow out — the glow is the point."
 },
 {
  "id": "0427",
  "sec": "arrival",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Six people walking in together. Nobody is looking at you. Stay wide, stay low, let the group read as a group."
 },
 {
  "id": "0490",
  "sec": "arrival",
  "tags": [
   "F",
   "U"
  ],
  "smile": 0,
  "note": "Sun straight down the corridor, four abreast, mid-stride. The best arrival frame we own. Get low, shoot into the light."
 },
 {
  "id": "0509",
  "sec": "arrival",
  "tags": [
   "F"
  ],
  "smile": 1,
  "note": "The greeting handshake with coffee still in hand. Shoot from the side so you get both faces, never from behind one of them."
 },
 {
  "id": "0526",
  "sec": "arrival",
  "tags": [
   "U"
  ],
  "smile": 1,
  "note": "Somebody clowns at the door — reward it instantly. Peace signs and open mouths are free joy. Take the frame."
 },
 {
  "id": "0439",
  "sec": "room",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Cold light, warm bodies. A room shot works when you can count silhouettes but still feel heat."
 },
 {
  "id": "0444",
  "sec": "room",
  "tags": [],
  "smile": 0,
  "note": "Shoot through something — a rail, a truss, a doorway. Foreground obstruction makes a crowd feel deep instead of flat."
 },
 {
  "id": "0445",
  "sec": "room",
  "tags": [],
  "smile": 0,
  "note": "Back of house, full haze, lights as beams. This is Carousel Slot 01. Take it every single Sunday."
 },
 {
  "id": "0448",
  "sec": "room",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "One raised fist against the wash. Expose for the light, let the person go completely black. Shape over detail."
 },
 {
  "id": "0453",
  "sec": "room",
  "tags": [],
  "smile": 0,
  "note": "Before doors. Empty stage, full haze, one figure. The calm-before frame that makes the next slide hit harder."
 },
 {
  "id": "0460",
  "sec": "room",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Ultra-wide, low, everybody's arms up. Use it once. It's the loudest shot in the folder and it dulls fast."
 },
 {
  "id": "0472",
  "sec": "room",
  "tags": [],
  "smile": 0,
  "note": "Room from the corner with the screen in frame. Shows the space, not just the people in it."
 },
 {
  "id": "0479",
  "sec": "room",
  "tags": [],
  "smile": 0,
  "note": "Straight down the center aisle with the rig overhead. Symmetry is the only time we'll accept a centered composition."
 },
 {
  "id": "0492",
  "sec": "room",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Wide, but faces still readable in the front row. That's the balance — scale that still has people in it."
 },
 {
  "id": "0497",
  "sec": "room",
  "tags": [],
  "smile": 0,
  "note": "Dark room, backs of heads, stage glowing small. Rare and worth having: shows a room paying attention."
 },
 {
  "id": "0422",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "A row of raised arms and one face turned up. Find the face. A frame of arms with no face is wallpaper."
 },
 {
  "id": "0423",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Somebody in the aisle, arms out, everyone around them still. Contrast is what makes a response read as a response."
 },
 {
  "id": "0425",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Three people, three postures, one moment. Groups beat singles when the postures differ."
 },
 {
  "id": "0431",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "A flag against the sun. Backlight anything held overhead — fabric, hands, a Bible — and it turns into a shape."
 },
 {
  "id": "0432",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Deep red wash, hands only. Don't correct the color. The room is that color and we want people to remember it."
 },
 {
  "id": "0433",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Tight on three faces in colored light. Under-shot and under-rated. Stand in the aisle and work one row for a full song."
 },
 {
  "id": "0437",
  "sec": "worship",
  "tags": [
   "J",
   "U"
  ],
  "smile": 1,
  "note": "Sunglasses indoors, headscarf, hand up, laughing. Style is part of the story. Shoot what people chose to wear."
 },
 {
  "id": "0441",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "One hand, sharp, everything behind it soft. Wide open at f/1.8. The simplest frame in the whole menu."
 },
 {
  "id": "0450",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Blue light, full smile, eyes squeezed shut. This is the single most valuable frame type we take. Chase it."
 },
 {
  "id": "0457",
  "sec": "worship",
  "tags": [
   "J",
   "U"
  ],
  "smile": 0,
  "note": "Front row losing it. Get in front of the front row, turn around, shoot back. Nobody thinks to do this."
 },
 {
  "id": "0466",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "A kid at full volume. Unfiltered is unbeatable. Get parent permission after — never skip the frame to ask first."
 },
 {
  "id": "0474",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Silhouette, arms wide, haze behind. Shoot from a crouch so the arms break the skyline."
 },
 {
  "id": "0483",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Hands open, eyes closed, mid-song. Honest and quiet. Balance the loud frames with three or four of these."
 },
 {
  "id": "0494",
  "sec": "worship",
  "tags": [
   "J",
   "U"
  ],
  "smile": 1,
  "note": "Black-and-white, novelty sunglasses, huge grin. Drop a few frames to mono — it makes the smile the only subject."
 },
 {
  "id": "0499",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Warm light, seated crowd, one hand up. Not every response is loud. Shoot the quiet ones too."
 },
 {
  "id": "0502",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Purple wash, mid-laugh, hands out. Colored light plus a real smile is the CLLXTV frame. If you get one thing, get this."
 },
 {
  "id": "0507",
  "sec": "worship",
  "tags": [
   "J",
   "U"
  ],
  "smile": 1,
  "note": "Dancing with no self-consciousness. Track her for four or five frames — the keeper is always mid-turn."
 },
 {
  "id": "0518",
  "sec": "worship",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Hands smeared by motion. Blur here is not a mistake, it is the proof that something was happening."
 },
 {
  "id": "0520",
  "sec": "worship",
  "tags": [
   "F",
   "J"
  ],
  "smile": 1,
  "note": "Three people, arms up, all laughing at once. Friendship and worship in the same frame. Rare. Take it every time you see it."
 },
 {
  "id": "0454",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Beam behind the head, mic at the mouth. Wait for the light to line up rather than firing on every phrase."
 },
 {
  "id": "0455",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Full purple haze, arm up. Let the color own the frame. Do not white-balance the room out of the photo."
 },
 {
  "id": "0456",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "The band gets one clean frame each. This is their whole year — don't shortchange the players for another lead vocal shot."
 },
 {
  "id": "0458",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Backlit silhouette, arms wide. Meter the light source, not the person."
 },
 {
  "id": "0459",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Mono, wide, the whole band working. Good closer frame for a stage set."
 },
 {
  "id": "0461",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Flat color behind, subject sharp. When the background is one solid field, get closer than feels comfortable."
 },
 {
  "id": "0462",
  "sec": "stage",
  "tags": [
   "U"
  ],
  "smile": 0,
  "note": "Confetti, wide, from the back. Pre-focus and burst. You get one shot at this and it lasts four seconds."
 },
 {
  "id": "0463",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Full-body, mid-motion, room lit behind. Stage shots need bodies, not just heads."
 },
 {
  "id": "0467",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Mono, tight on the face mid-word. The most human stage frame in the reference set."
 },
 {
  "id": "0468",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Crouched, mic down, eyes shut. Shoot the delivery, not the pose between lines."
 },
 {
  "id": "0475",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "From behind, arms open toward the room. The preacher-facing-the-people frame. Shows the room without a second camera."
 },
 {
  "id": "0477",
  "sec": "stage",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Two people on stage in warm light. Interaction on stage reads as team, and team reads as family."
 },
 {
  "id": "0478",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Arm extended toward the crowd, from behind. This is the connective shot between §Stage and §Worship."
 },
 {
  "id": "0480",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Low, moody, keys and mic. Worth three or four frames when the light is doing something interesting."
 },
 {
  "id": "0481",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Worship lead mid-phrase, full body, room visible. Wide enough to place her in the space."
 },
 {
  "id": "0501",
  "sec": "stage",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Stage in the frame and a family in the foreground. Never shoot the stage alone if a person can be in it."
 },
 {
  "id": "0505",
  "sec": "stage",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Preacher sharp, raised hands soft in the foreground. Shoot past the crowd, not around it. Best stage frame we take."
 },
 {
  "id": "0506",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Silhouetted bassist in magenta. Instruments make good shapes. Use them."
 },
 {
  "id": "0513",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Hands clasped, tight, teal light. The pause in the message is more photogenic than the point."
 },
 {
  "id": "0514",
  "sec": "stage",
  "tags": [],
  "smile": 0,
  "note": "Full body, hand on hip, mic up. Every message needs one clean full-length frame for the recap."
 },
 {
  "id": "0521",
  "sec": "stage",
  "tags": [
   "F"
  ],
  "smile": 1,
  "note": "Guitarist playing to one person at the edge of the stage. Watch the edges — that's where the stage meets the room."
 },
 {
  "id": "0428",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 1,
  "note": "Three friends, pointing, cracking up. Posed friendship still photographs as friendship. Let people ham it up."
 },
 {
  "id": "0440",
  "sec": "tribe",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Portrait, natural light, real laugh. Three frames — the third is the keeper. Then show him the back of the camera."
 },
 {
  "id": "0442",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 0,
  "note": "A table, drinks, everybody talking at once. Nobody looks at the lens. Shoot from four feet and stay quiet."
 },
 {
  "id": "0443",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "The hug, shot over one shoulder so you get the other person's face. If you can't see a face, move."
 },
 {
  "id": "0447",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Two people sitting, turned toward each other. Distance between them is the whole story — keep it small."
 },
 {
  "id": "0451",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 1,
  "note": "Two friends, one laughing at the other. Shoot the listener, not the talker. The listener has the better face."
 },
 {
  "id": "0452",
  "sec": "tribe",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Straight-on, close, wide open, full smile. Chest height, never above. This is the 10-Second Portrait done right."
 },
 {
  "id": "0464",
  "sec": "tribe",
  "tags": [
   "U"
  ],
  "smile": 1,
  "note": "Pizza, mid-bite, no dignity. Food frames prove church is fun faster than any other shot we take."
 },
 {
  "id": "0465",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Two people walking away together. Quietly the most powerful frame in this deck — nobody left alone."
 },
 {
  "id": "0469",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "A group standing around doing nothing in particular. That's the shot. Belonging looks like standing around."
 },
 {
  "id": "0471",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "A hug in haze, backlit, faces barely readable. Atmosphere can carry a frame when the emotion is obvious."
 },
 {
  "id": "0473",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 0,
  "note": "Faces packed edge to edge, color in the foreground. Fill the frame with people and let it get messy."
 },
 {
  "id": "0476",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "From behind, walking out into the trees. Leaving together is as much the story as arriving together."
 },
 {
  "id": "0485",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Sitting on the ground, spread out, unhurried. Shoot the slow parts of the day, not only the peaks."
 },
 {
  "id": "0486",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 1,
  "note": "Three friends who asked to be photographed. Say yes every time. Posed frames are 30% of the delivered gallery."
 },
 {
  "id": "0487",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 1,
  "note": "Long table, food, both sides of the frame full. Shoot down the length of the table from the end."
 },
 {
  "id": "0488",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Families and kids in the same frame as adults. Shows a room that has room for everybody."
 },
 {
  "id": "0489",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 0,
  "note": "Two friends, arms up, golden hour. Backlight plus friendship. Easiest great photo available to you."
 },
 {
  "id": "0491",
  "sec": "tribe",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Two people in haze and warm light, mid-conversation. Long lens, from distance, no interruption."
 },
 {
  "id": "0510",
  "sec": "tribe",
  "tags": [
   "F",
   "U"
  ],
  "smile": 1,
  "note": "A whole table laughing at the same thing. Wait for the punchline — you can hear it coming."
 },
 {
  "id": "0527",
  "sec": "tribe",
  "tags": [
   "U"
  ],
  "smile": 0,
  "note": "From behind: a bow, a peace sign, no face at all. You don't always need the face to sell the mood."
 },
 {
  "id": "0482",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "On her knees at the front, others standing. Wide enough to include the people who stayed with her."
 },
 {
  "id": "0484",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "Two men, one praying for the other, outdoors. Breakthrough doesn't only happen at the altar. Watch the edges of the property."
 },
 {
  "id": "0496",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "Hands on a head, faces mostly hidden. Long lens, ten feet back. Never step into the circle."
 },
 {
  "id": "0498",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "Kneeling in colored light. Shoot it, then flag it HOLD until you've asked her. Take the frame first, always."
 },
 {
  "id": "0500",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "Laying on of hands with the room pressing in. Scale plus intimacy in one frame."
 },
 {
  "id": "0512",
  "sec": "breakthrough",
  "tags": [
   "B",
   "J"
  ],
  "smile": 0,
  "note": "Full altar, wide, from the back. This is how we show how many people responded without singling anyone out."
 },
 {
  "id": "0515",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "A circle outside, hands out, daylight. Easier to shoot than the indoor altar and just as true."
 },
 {
  "id": "0517",
  "sec": "breakthrough",
  "tags": [
   "B"
  ],
  "smile": 0,
  "note": "Mono, tight, three people holding one. Black-and-white is the right call when the light is ugly and the moment isn't."
 },
 {
  "id": "0523",
  "sec": "breakthrough",
  "tags": [
   "F",
   "B"
  ],
  "smile": 0,
  "note": "Two men embracing after. This is the frame that proves the fourth promise — not the tears, the moment after them."
 },
 {
  "id": "0429",
  "sec": "after",
  "tags": [
   "F",
   "J"
  ],
  "smile": 1,
  "note": "Post-service, four women, all laughing. The lobby after service is the highest smile-yield ten minutes of the day."
 },
 {
  "id": "0438",
  "sec": "after",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Clean portrait, soft background, easy smile. Ask, shoot three, show her. Thirty seconds, one keeper."
 },
 {
  "id": "0495",
  "sec": "after",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Caught mid-laugh in the ambient light. Don't set it up. Stand still for two minutes and it comes to you."
 },
 {
  "id": "0503",
  "sec": "after",
  "tags": [
   "J"
  ],
  "smile": 1,
  "note": "Mono portrait, walking, full grin. Shoot people while they're moving toward you — it beats standing them still."
 },
 {
  "id": "0508",
  "sec": "after",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Three men walking out together. Backs are fine here. The read is 'we came together, we're leaving together.'"
 },
 {
  "id": "0511",
  "sec": "after",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "A circle in the parking lot that hasn't broken up yet. Nobody wants to leave. Photograph that."
 },
 {
  "id": "0525",
  "sec": "after",
  "tags": [
   "F"
  ],
  "smile": 1,
  "note": "Two guys talking outside long after service ended. Shoot the conversation that outlasts the service."
 },
 {
  "id": "0528",
  "sec": "after",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Drinks in hand, standing, talking. Low light, high ISO, don't flash it. Grain is fine. Blur is not."
 },
 {
  "id": "0430",
  "sec": "texture",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "Sun straight into the lens, subject in silhouette. Flare is texture. Stop fighting it."
 },
 {
  "id": "0434",
  "sec": "texture",
  "tags": [
   "U"
  ],
  "smile": 0,
  "note": "A hat held up over a crowd. Objects overhead against the sky are instant graphic shapes."
 },
 {
  "id": "0435",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "Back of a jacket, text readable, crowd behind. Clothing tells you who's in the room. Shoot the merch, the print, the patch."
 },
 {
  "id": "0436",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "Jewelry, skin, shallow focus. Fills Carousel Slot 03. Get four or five of these every Sunday."
 },
 {
  "id": "0449",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "A phone recording the stage. The frame inside a frame. Always available, always useful."
 },
 {
  "id": "0470",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "A person against a wall of handwriting. Environmental detail that says where we are without a caption."
 },
 {
  "id": "0493",
  "sec": "texture",
  "tags": [
   "F"
  ],
  "smile": 0,
  "note": "Held hands, cropped tight, no faces. Proves connection with zero consent risk. Shoot more of these."
 },
 {
  "id": "0504",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "A photo being shared on a phone. Worth documenting: the frame leaving the building is the point of all of this."
 },
 {
  "id": "0519",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "Board, faders, mono. Production is part of the story. Ten seconds of your day, one good frame."
 },
 {
  "id": "0522",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "Another shooter working the crowd. We document us too — one frame per Sunday."
 },
 {
  "id": "0524",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "Something being carried out. Hands and objects fill a grid without repeating a face."
 },
 {
  "id": "0529",
  "sec": "texture",
  "tags": [],
  "smile": 0,
  "note": "Someone taking notes during the message. Quiet, specific, and it says people are actually listening."
 },
 {
  "id": "0530",
  "sec": "texture",
  "tags": [
   "J"
  ],
  "smile": 0,
  "note": "One hand up against a light bar. Simple graphic frame — shape, light, nothing else."
 }
];
